import { useEffect, useCallback, useRef } from 'react';

/**
 * useImagePreloader - Hook for intelligent image preloading
 *
 * Features:
 * - Preloads next N images in sequence
 * - Hover-based preloading
 * - Viewport-based preloading
 * - Memory management to prevent excessive loading
 */
const useImagePreloader = ({
  images = [],
  currentIndex = 0,
  preloadCount = 2,
  enabled = true
}) => {
  const preloadCache = useRef(new Set());
  const preloadPromises = useRef(new Map());

  // Preload a single image
  const preloadImage = useCallback((imagePath) => {
    if (!imagePath || preloadCache.current.has(imagePath)) {
      return Promise.resolve();
    }

    // Return existing promise if already loading
    if (preloadPromises.current.has(imagePath)) {
      return preloadPromises.current.get(imagePath);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        preloadCache.current.add(imagePath);
        preloadPromises.current.delete(imagePath);
        resolve(imagePath);
      };

      img.onerror = () => {
        preloadPromises.current.delete(imagePath);
        reject(new Error(`Failed to preload: ${imagePath}`));
      };

      // Load JPEG directly (WebP disabled - server doesn't have WebP files)
      img.src = imagePath;
    });

    preloadPromises.current.set(imagePath, promise);
    return promise;
  }, []);

  // Build image path from photo object
  const buildImagePath = useCallback((photo, category, thumbnail = false) => {
    if (photo.src) return photo.src;

    // Check if photo object has pre-defined URLs (ElizasAward.js style)
    if (thumbnail && photo.thumbnailUrl) return photo.thumbnailUrl;
    if (!thumbnail && photo.localUrl) return photo.localUrl;

    // Fallback to path construction (Gallery.js style)
    const basePath = '/uploads';
    const categoryPath = category?.title?.toLowerCase().replace(/\s+/g, '-') || 'hospital-journey';
    const sizePath = thumbnail ? 'thumbs' : 'optimized';

    return `${basePath}/${categoryPath}/${sizePath}/${photo.filename}`;
  }, []);

  // Preload next images in sequence
  const preloadNextImages = useCallback((fromIndex = currentIndex) => {
    if (!enabled || !images.length) return;

    const startIndex = Math.max(0, fromIndex);
    const endIndex = Math.min(images.length, startIndex + preloadCount);

    for (let i = startIndex; i < endIndex; i++) {
      const photo = images[i];
      if (!photo) continue;

      // Preload both thumbnail and full image
      const thumbnailPath = buildImagePath(photo, photo.category, true);
      const fullImagePath = buildImagePath(photo, photo.category, false);

      preloadImage(thumbnailPath).catch(err =>
        console.debug('Thumbnail preload failed:', err.message)
      );

      // Preload full image with lower priority
      setTimeout(() => {
        preloadImage(fullImagePath).catch(err =>
          console.debug('Full image preload failed:', err.message)
        );
      }, 500);
    }
  }, [enabled, images, currentIndex, preloadCount, buildImagePath, preloadImage]);

  // Preload previous images (for back navigation)
  const preloadPreviousImages = useCallback((fromIndex = currentIndex) => {
    if (!enabled || !images.length) return;

    const startIndex = Math.max(0, fromIndex - preloadCount);
    const endIndex = Math.min(images.length, fromIndex);

    for (let i = startIndex; i < endIndex; i++) {
      const photo = images[i];
      if (!photo) continue;

      const thumbnailPath = buildImagePath(photo, photo.category, true);
      preloadImage(thumbnailPath).catch(err =>
        console.debug('Previous thumbnail preload failed:', err.message)
      );
    }
  }, [enabled, images, currentIndex, preloadCount, buildImagePath, preloadImage]);

  // Hover preloading
  const preloadOnHover = useCallback((targetIndex) => {
    if (!enabled || targetIndex < 0 || targetIndex >= images.length) return;

    const photo = images[targetIndex];
    if (!photo) return;

    const fullImagePath = buildImagePath(photo, photo.category, false);
    preloadImage(fullImagePath).catch(err =>
      console.debug('Hover preload failed:', err.message)
    );
  }, [enabled, images, buildImagePath, preloadImage]);

  // Effect to preload images when currentIndex changes
  useEffect(() => {
    if (!enabled) return;

    // Preload next images
    preloadNextImages(currentIndex + 1);

    // Preload previous images (for back navigation)
    preloadPreviousImages(currentIndex - 1);
  }, [currentIndex, enabled, preloadNextImages, preloadPreviousImages]);

  // Clear cache when component unmounts or images change
  useEffect(() => {
    const cache = preloadCache.current;
    const promises = preloadPromises.current;

    return () => {
      cache.clear();
      promises.clear();
    };
  }, [images]);

  return {
    preloadNextImages,
    preloadPreviousImages,
    preloadOnHover,
    preloadImage,
    isPreloaded: (imagePath) => preloadCache.current.has(imagePath),
    preloadCacheSize: preloadCache.current.size
  };
};

export default useImagePreloader;